import { debugLog } from '../utils/debug';
import { TokenManager, UserInfo } from './TokenManager';
import { ApiService } from '../utils/ApiService';
import { DeviceFingerprint } from '../utils/DeviceFingerprint';
import { PORTAL_URL } from '../config/addinNamespace';

interface OfficeRuntimeAuthOptions {
  allowSignInPrompt?: boolean;
  allowConsentPrompt?: boolean;
  forMSGraphAccess?: boolean;
}

declare const OfficeRuntime: {
  auth: {
    getAccessToken(options?: OfficeRuntimeAuthOptions): Promise<string>;
  };
};

export interface SSOResult {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: UserInfo;
  error?: string;
  requiresFallback?: boolean;
}

export class SSOService {
  private tokenManager: TokenManager;
  private apiService: ApiService;
  private deviceFingerprint: DeviceFingerprint;

  constructor() {
    this.tokenManager = new TokenManager();
    this.apiService = new ApiService();
    this.deviceFingerprint = new DeviceFingerprint();
  }

  async trySilentSSO(): Promise<SSOResult> {
    try {
      if (!this.isSSOSupported()) {
        return {
          success: false,
          requiresFallback: true,
          error: 'SSO not supported in this environment'
        };
      }

      const officeToken = await this.getOfficeAccessToken(false);

      if (!officeToken) {
        return {
          success: false,
          requiresFallback: true,
          error: 'Silent SSO not available'
        };
      }

      return this.completeSSO(officeToken);
    } catch (error) {
      console.error('Silent SSO error:', error);
      return {
        success: false,
        requiresFallback: true,
        error: error instanceof Error ? error.message : 'Silent SSO failed'
      };
    }
  }

  async trySSO(): Promise<SSOResult> {
    try {
      if (!this.isSSOSupported()) {
        return {
          success: false,
          requiresFallback: true,
          error: 'SSO not supported in this environment'
        };
      }

      const officeToken = await this.getOfficeAccessToken(true);

      if (!officeToken) {
        return {
          success: false,
          requiresFallback: true,
          error: 'Could not get Office access token'
        };
      }

      return this.completeSSO(officeToken);
    } catch (error) {
      console.error('SSO error:', error);
      return {
        success: false,
        requiresFallback: true,
        error: error instanceof Error ? error.message : 'SSO failed'
      };
    }
  }

  private async completeSSO(officeToken: string): Promise<SSOResult> {
    const backendResult = await this.exchangeTokenWithBackend(officeToken);

    if (backendResult.success && backendResult.token && backendResult.user) {
      await this.tokenManager.storeToken(
        backendResult.token,
        backendResult.user,
        backendResult.refreshToken
      );
    }

    return backendResult;
  }

  private isSSOSupported(): boolean {
    try {
      return typeof OfficeRuntime !== 'undefined' &&
             OfficeRuntime.auth !== undefined &&
             typeof OfficeRuntime.auth.getAccessToken === 'function';
    } catch {
      return false;
    }
  }

  private async getOfficeAccessToken(interactive: boolean): Promise<string | null> {
    try {
      const options = {
        allowSignInPrompt: interactive,
        allowConsentPrompt: interactive,
        forMSGraphAccess: false
      };

      const token = await OfficeRuntime.auth.getAccessToken(options);
      return token;

    } catch (error) {
      console.error('Office SSO error:', error);
      const ssoError = error as { code?: number };

      if (ssoError.code === 13001) {
        debugLog('User not signed in to Office');
      } else if (ssoError.code === 13002) {
        debugLog('User cancelled SSO');
      } else if (ssoError.code === 13003) {
        debugLog('SSO not configured correctly');
      } else if (ssoError.code === 13004 || ssoError.code === 13005) {
        debugLog('Office SSO not available');
      } else if (ssoError.code === 13007) {
        debugLog('Office version does not support SSO');
      } else if (ssoError.code === 13008) {
        debugLog('User needs to consent');
      } else if (ssoError.code === 13012) {
        debugLog('API not supported on this platform');
      }

      return null;
    }
  }

  private async exchangeTokenWithBackend(
    officeToken: string,
    confirmNewCompany: boolean = false,
    acceptTerms: boolean = false
  ): Promise<SSOResult> {
    try {
      const fingerprint = await this.deviceFingerprint.generate();

      const claims = this.decodeJwtClaims(officeToken);

      // `/auth/microsoft/sync` is public, so the backend derives every identity
      // claim from the signed token and ignores the body. Sending `oid`/`tid`/
      // `email` alongside it only invites someone to believe they are read.
      // The fingerprint is a header because the body is not trusted either.
      const response = await this.apiService.post('/auth/microsoft/sync', {
        microsoftIdToken: officeToken,
        confirmNewCompany,
        acceptTerms
      }, {
        headers: { 'x-device-fingerprint': fingerprint }
      });

      if (response.success) {
        const data = response.data as { requiresConfirmation?: boolean; requiresTerms?: boolean; code?: string; message?: string; domain?: string; companyName?: string; accessToken?: string; refreshToken?: string; user?: { email?: string; id?: string; company?: { name?: string }; role?: string } };

        // Signing in would create an account (invitation or domain join): the
        // backend creates nothing until the terms are accepted.
        if (data.requiresTerms && data.code === 'TERMS_REQUIRED') {
          const accepted = await this.showSignInDialog({
            title: 'Accept the terms',
            body: data.companyName
              ? `You're joining ${data.companyName} in Bifrost Ledger. Accept the terms to finish creating your account.`
              : 'Accept the terms to finish creating your Bifrost Ledger account.',
            confirmLabel: 'Continue',
          });
          if (accepted) {
            return this.exchangeTokenWithBackend(officeToken, confirmNewCompany, true);
          }
          return { success: false, requiresFallback: true, error: 'Terms not accepted' };
        }

        if (data.requiresConfirmation && data.code === 'NEW_COMPANY_CONFIRMATION') {
          const userConfirmed = await this.showSignInDialog({
            title: 'Create New Company?',
            body: data.message || `No company found for domain "${data.domain || ''}". A new company will be created and you will be its administrator.`,
            confirmLabel: 'Create Company',
          });

          if (userConfirmed) {
            // Retry with confirmation; the dialog also took the terms.
            return this.exchangeTokenWithBackend(officeToken, true, true);
          } else {
            return {
              success: false,
              requiresFallback: true,
              error: 'Company registration cancelled'
            };
          }
        }

        const accessToken: string = data.accessToken || '';
        const refreshToken: string = data.refreshToken || '';
        const user = data.user || {};

        // Our own access token names the user and company; the Microsoft claims do not.
        let session: { userId?: string; companyId?: string } = {};
        try {
          session = this.tokenManager.decodeJWT(accessToken) as typeof session;
        } catch {
          // Not decodable: the stored session falls back to the token on read (TokenManager.getUserInfo).
        }
        const userInfo: UserInfo = {
          email: (user?.email || claims?.preferred_username || '') as string,
          userId: (session.userId || user?.id || claims?.oid || '') as string,
          company: (user?.company?.name || '') as string,
          companyId: session.companyId,
          permissions: [(user?.role || 'USER') as string]
        };

        return {
          success: true,
          token: accessToken,
          refreshToken,
          user: userInfo
        };
      } else {
        return {
          success: false,
          requiresFallback: true,
          error: response.error || 'Backend sync failed'
        };
      }

    } catch (error) {
      console.error('Backend token exchange error:', error);
      return {
        success: false,
        requiresFallback: true,
        error: error instanceof Error ? error.message : 'Token exchange failed'
      };
    }
  }

  /**
   * A confirm dialog that always carries the terms checkbox: both questions it
   * asks create an account. The confirm button stays disabled until ticked.
   */
  private showSignInDialog(options: { title: string; body: string; confirmLabel: string }): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:10000;display:flex;align-items:center;justify-content:center;';

      const dialog = document.createElement('div');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'sso-dialog-title');
      dialog.setAttribute('aria-describedby', 'sso-dialog-body');
      dialog.style.cssText = 'background:white;border-radius:8px;padding:24px;max-width:360px;margin:16px;box-shadow:0 4px 24px rgba(0,0,0,0.2);';

      const title = document.createElement('h3');
      title.id = 'sso-dialog-title';
      title.textContent = options.title;
      title.style.cssText = 'margin:0 0 12px 0;font-size:16px;font-weight:600;color:#1a1a1a;';

      const body = document.createElement('p');
      body.id = 'sso-dialog-body';
      body.textContent = options.body;
      body.style.cssText = 'margin:0 0 16px 0;font-size:14px;color:#555;line-height:1.5;';

      const termsLabel = document.createElement('label');
      termsLabel.style.cssText = 'display:flex;gap:8px;align-items:flex-start;margin:0 0 20px 0;font-size:13px;color:#333;line-height:1.4;';
      const termsBox = document.createElement('input');
      termsBox.type = 'checkbox';
      termsBox.style.cssText = 'margin-top:2px;';
      const termsText = document.createElement('span');
      const link = (text: string, path: string) => {
        const a = document.createElement('a');
        a.textContent = text;
        a.href = `${PORTAL_URL}${path}`;
        a.target = '_blank';
        a.rel = 'noopener';
        a.style.cssText = 'color:#2563eb;';
        return a;
      };
      // appendChild, not append(): desktop Excel can host the pane in the
      // IE11 engine, which has no ParentNode.append.
      [
        document.createTextNode('I have read and accept the '),
        link('Terms of Service', '/legal/terms'),
        document.createTextNode(' and the '),
        link('Privacy Policy', '/legal/privacy'),
        document.createTextNode('.'),
      ].forEach(node => termsText.appendChild(node));
      termsLabel.appendChild(termsBox);
      termsLabel.appendChild(termsText);

      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;';

      // removeChild, not remove(): the IE11 engine desktop Excel can use has no remove().
      const cleanup = () => { overlay.parentNode?.removeChild(overlay); };

      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = 'Cancel';
      cancelBtn.style.cssText = 'padding:8px 16px;border:1px solid #ddd;border-radius:4px;background:white;cursor:pointer;font-size:14px;';
      cancelBtn.onclick = () => { cleanup(); resolve(false); };

      const confirmBtn = document.createElement('button');
      confirmBtn.textContent = options.confirmLabel;
      confirmBtn.disabled = true;
      confirmBtn.style.cssText = 'padding:8px 16px;border:none;border-radius:4px;background:#2563eb;color:white;cursor:pointer;font-size:14px;font-weight:500;opacity:0.5;';
      termsBox.onchange = () => {
        confirmBtn.disabled = !termsBox.checked;
        confirmBtn.style.opacity = termsBox.checked ? '1' : '0.5';
      };
      confirmBtn.onclick = () => { if (!termsBox.checked) return; cleanup(); resolve(true); };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { cleanup(); resolve(false); }
      };
      overlay.addEventListener('keydown', onKeyDown);

      btnRow.appendChild(cancelBtn);
      btnRow.appendChild(confirmBtn);
      dialog.appendChild(title);
      dialog.appendChild(body);
      dialog.appendChild(termsLabel);
      dialog.appendChild(btnRow);
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      termsBox.focus();
    });
  }

  private decodeJwtClaims(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }

      const payload = atob(base64);
      return JSON.parse(payload);
    } catch (e) {
      console.error('JWT decode error:', e);
      return null;
    }
  }
}
